import type { V1AppConfigGetLoginBrandingResponse } from '@repo/sdk/gen'

/**
 * What the app draws as the product: the instance's runtime branding and
 * nothing else (`web/apps/keycloak-theme/LOGIN-BRANDING.md`).
 *
 * THERE IS NO FALLBACK IDENTITY. The operator publishing nothing, the read
 * failing, or one field being unset all arrive here as an absent value and
 * leave the field absent: no product name, no mark. Every consumer draws
 * nothing branded for an absent field (no `<img>`, no name, no favicon link),
 * so an unconfigured instance looks unbranded instead of wearing someone
 * else's identity.
 *
 * THE DARK MARK FOLLOWS THE LIGHT ONE. A published mark with no reversed twin
 * is drawn on both surfaces, as the login page does. The switch between the
 * two stays the `.dark` class at every call site, never
 * `prefers-color-scheme`: someone can choose the dark theme while their system
 * is set to light, and only the class knows which is on screen.
 *
 * THE SIGN-IN PAGES CARRY THE BACKDROP AND THE COPYRIGHT, like Keycloak's
 * page: `canvas` is `colors.background` / `colors.backgroundDark` with the
 * optional images, and `copyright` the localized line, `{year}` unreplaced.
 *
 * This client draws marks, not the lockup: the sidebar's top slot shows the
 * section and the organization beside the mark, so there is no consumer for a
 * wide logo here. The back office has one and resolves it in its own copy.
 */
export function resolveBrand(
	instance?: V1AppConfigGetLoginBrandingResponse['instance']
) {
	const mark = instance?.images.mark?.url
	const markDark = instance?.images.markDark?.url

	const background = instance?.tokens.light.canvas
	const backgroundDark = instance?.tokens.dark.canvas

	return {
		// Whether the installation lets a person or an organization have no
		// storage limit; absent or null means no.
		allowUncappedQuotas: instance?.allowUncappedQuotas === true,
		productName: instance?.productName,
		mark: mark ? { light: mark, dark: markDark ?? mark } : undefined,
		favicon: instance?.images.favicon?.url,
		// The sign-in pages' backdrop. The dark image is never borrowed from the
		// light one: a picture made for a light page rarely suits a dark one, so
		// the dark colour then shows alone. Absent when no colour is published.
		canvas:
			background || backgroundDark
				? {
						light: background,
						dark: backgroundDark,
						image: instance?.images.background?.url,
						imageDark: instance?.images.backgroundDark?.url
					}
				: undefined,
		copyright: instance?.texts.copyright,
		// Where the «too large for the browser» dialog sends people; no link
		// is drawn when the operator has not published one.
		desktopClientUrl: instance?.desktopClientUrl
	}
}
